import DeleteWarningIcon from '@mui/icons-material/WarningAmberRounded'
import { Box, useTheme } from '@mui/material'
import { GridColumns } from '@mui/x-data-grid-pro'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { invalidationActivityLogCache } from 'src/api/activity-log'
import { invalidateCustomer } from 'src/api/customer'
import { EmptyStatusCard } from 'src/components/mooring/EmptyStatusCard'
import InfiniteScrollTable from 'src/components/table/InfiniteScrollTable'
import { useNavigation } from 'src/context/NavigationContext'
import { permissionValue } from 'src/enums/permissionValue'
import { useDialogCtx } from 'src/hooks/context-hooks'
import { deviationQueryKeys } from 'src/query-keys'
import { deviationsEndpoints, invalidateDeviation, useDeleteDeviation } from '../../../api/deviation'
import { AppRoutesNavigation } from '../../../app-routes'
import NoDeviation from '../../../assets/svg/no-deviation.svg'
import { hasPermission } from '../../../components/common/Authorization'
import UpdateDeviationDialog from '../../../dialogs/deviation/UpdateDeviationDialog'
import { IDeviation } from '../../../generated-types/deviation'
import { IDeviationTableContainerProps } from './DeviationsTable'
import { minimal_data_deviation_static_column } from './MinimalDataDeviationsTable.action'
import { DeleteOutlineOutlined, EditOutlined } from '@mui/icons-material'
import { GridActionButton } from 'src/components/common/GridActionButton'
import { invalidateMainComponentCache } from 'src/api/main-component'

export const MinimalDataDeviationsTable: React.FC<IDeviationTableContainerProps> = ({
  customerId,
  localityId,
  disabled,
  queryParams,
  handleRowClick,
  updatePermissions,
  filterDeviation,
  pop,
  height,
  autoHeight,
  maxHeight,
  deletePermissions,
  permission,
  enableDelete = false,
  serviceStationId,
  storageId,
  emptyStatusHeight,
  emptyStatusImageWidth,
  emptyStatusContent,
  emptyStatusTitle,
  emptyStatusContentFontSize,
  emptyStatusTitleFontSize,
  selectedLocalityId,
  maximumVisibleRowCount,
  minimalEmptyStatus,
  isLocalityPortal,
}) => {
  const { showDialog, showConfirmDialog, closeAllDialogs } = useDialogCtx()
  const { navigateTo } = useNavigation()
  const theme = useTheme()
  const { t } = useTranslation(['deviation', 'common'])

  const mutation = useDeleteDeviation(customerId)
  const onDelete = (id: EntityId) => {
    showConfirmDialog(
      t('deleteDeviation'),
      t('areYouSureDeleteDeviation'),
      {
        acceptText: t('yes', { ns: 'common' }),
        cancelText: t('no', { ns: 'common' }),
      },
      pop => {
        mutation.mutate(id, {
          onSuccess: () => {
            toast.success(t('deviationDeletedSuccessfully'))
            invalidateDeviation.getDeviationsAdmin()
            invalidateDeviation.invalidateAllDeviations(customerId, localityId, undefined, undefined)
            invalidateDeviation.getAllDeviationsForScheduledWorkOrders(customerId as EntityId, localityId as EntityId)
            invalidateCustomer.getCustomersActivityCounts(customerId)
            invalidationActivityLogCache.getCageActivityLogs()
            invalidateDeviation.getDeviations(customerId)
            invalidateMainComponentCache.getMainComponents(customerId as EntityId, localityId as EntityId)
            pop()
          },
        })
      },
      {
        Icon: (
          <DeleteWarningIcon
            sx={{ height: '43px', width: '48px', color: theme.palette.errorMain[theme.palette.mode] }}
          />
        ),
        iconBGColor: '#D32F2F4D',
        buttonTextColor: theme.palette.white[theme.palette.mode],
        buttonBGColor: theme.palette.errorMain[theme.palette.mode],
      },
    )
  }

  const updateDeviation = useCallback(
    async (deviation: IDeviation) => {
      const updated = await showDialog(
        UpdateDeviationDialog,
        {
          componentProps: {
            customerId: customerId as EntityId,
            localityId: localityId,
            deviation: deviation,
            selectedLocalityId: selectedLocalityId,
            permission,
          },
        },
        undefined,
        true,
      )
      if (!updated) return
    },
    [customerId, localityId, selectedLocalityId, permission, showDialog],
  )

  const columns: GridColumns = useMemo(
    () => [
      ...minimal_data_deviation_static_column(t, theme, navigateTo, customerId, localityId as EntityId, pop, disabled),
      {
        field: 'actions',
        headerName: t('actions', { ns: 'common' }),
        flex: 0.5,
        minWidth: 100,
        maxWidth: 120,
        headerAlign: 'right',
        align: 'right',
        renderCell: ({ id, row }) => (
          <Box display="flex" gap={0.5} justifyContent="flex-end" alignItems="center" width="100%">
            <GridActionButton
              buttonIcon={<EditOutlined sx={{ width: '16px' }} />}
              toolTipTitle={t('edit', { ns: 'common' })}
              onClick={e => {
                e.stopPropagation()
                updateDeviation(row)
              }}
              width={'24px'}
              height={'24px'}
              disabled={(hasPermission(updatePermissions, permission) ? false : true) || disabled || row.status}
            ></GridActionButton>
            {enableDelete && (
              <GridActionButton
                buttonIcon={<DeleteOutlineOutlined sx={{ width: '16px' }} />}
                toolTipTitle={t('delete', { ns: 'common' })}
                onClick={e => {
                  e.stopPropagation()
                  onDelete(id)
                }}
                width={'24px'}
                height={'24px'}
                disabled={(hasPermission(deletePermissions, permission) ? false : true) || disabled || row.status}
              ></GridActionButton>
            )}
          </Box>
        ),
      },
    ],
    [
      disabled,
      t,
      theme,
      updateDeviation,
      updatePermissions,
      navigateTo,
      customerId,
      localityId,
      pop,
      onDelete,
      enableDelete,
      deletePermissions,
      permission,
    ],
  )

  const onRowClick = useCallback(
    cell => {
      if (
        disabled ||
        (permission &&
          !hasPermission(
            [...updatePermissions, ...deletePermissions, permissionValue.Locality.MainComponent.Deviation.Read],
            permission,
          ))
      )
        return
      closeAllDialogs()

      if (((localityId ?? selectedLocalityId) && isLocalityPortal) && serviceStationId == null && storageId == null)
        navigateTo(
          AppRoutesNavigation.Localities.deviation(customerId, (localityId ?? selectedLocalityId) as EntityId, cell.id),
        )
      else if (serviceStationId)
        navigateTo(AppRoutesNavigation.serviceStation.deviation(customerId, serviceStationId as EntityId, cell.id))
      else if (storageId) {
        navigateTo(AppRoutesNavigation.storage.deviation(customerId, storageId as EntityId, cell.id))
      } else navigateTo(AppRoutesNavigation.Customer.deviation(customerId, cell.id))
    },
    [customerId, disabled, localityId, navigateTo],
  )

  return (
    <InfiniteScrollTable<IDeviation>
      id={'washing-reports-table'}
      queryUrl={deviationsEndpoints.getDeviations(customerId)}
      generateQueryKey={(page: number, pageSize: number) =>
        deviationQueryKeys.GET_DEVIATIONS(customerId, {
          ...queryParams,
          page,
          pageSize,
          ...filterDeviation,
        })
      }
      onRowClick={handleRowClick ?? onRowClick}
      queryParams={{ ...queryParams }}
      autoHeight={autoHeight}
      maxVisibleRowCount={maximumVisibleRowCount}
      pageSize={10}
      getRowHeight={() => 41}
      minRowHeight="41px"
      height={height}
      sx={{ ...(maxHeight && { maxHeight: `${maxHeight}px` }) }}
      containerProps={{
        sx: {
          '& .MuiDataGrid-virtualScroller': {
            paddingBottom: '0px !important',
            overflowX: 'hidden',
          },
          '& .MuiDataGrid-virtualScrollerContent': {
            paddingBottom: '0px !important',
          },
        },
      }}
      columns={columns}
      spinnerHeight={100}
      noDataComponent={() => (
        <Box height={'100%'}>
          <EmptyStatusCard
            image={NoDeviation}
            header={emptyStatusTitle ?? t('deviation-empty-status-title', { ns: 'deviation' })}
            content={emptyStatusContent ?? t('deviation-empty-status-content', { ns: 'deviation' })}
            imageWidth={emptyStatusImageWidth ?? '162px'}
            imageContainerClasses="p-3"
            contentTopMargin="10px"
            height={emptyStatusHeight}
            contentFontSize={emptyStatusContentFontSize}
            headerFontSize={emptyStatusTitleFontSize}
            imageTopMargin="0px"
            contentClasses="mt-0"
            hideImage={minimalEmptyStatus}
            compact={minimalEmptyStatus}
          />
        </Box>
      )}
      hideDefaultFooterPagination
    />
  )
}
